import type { AnswerValue, Question } from "@/schema/quizSchema";

/**
 * Single source of truth for how each Question type advances:
 * - "immediate": answering advances the quiz right away (single choice, info).
 * - "staged": the answer is staged as a draft; the user advances via the Next
 *   control (multi/slider/text/number).
 *
 * Replaces the three previously-scattered per-type conditionals in QuizWidget
 * (onAnswer auto-advance check, the Next-button render check, the default-answer
 * enumeration), so a new question type is wired by adding one row here.
 */
export const ADVANCE_MODE: Record<Question["type"], "immediate" | "staged"> = {
  single: "immediate",
  info: "immediate",
  multi: "staged",
  slider: "staged",
  text: "staged",
  number: "staged",
};

/** True when answering this question advances the quiz immediately. */
export function advancesImmediately(question: Question): boolean {
  return ADVANCE_MODE[question.type] === "immediate";
}

/**
 * The answer used when a staged question is advanced via Next with no draft
 * entered. Immediate types return null (single advances with the chosen value,
 * info with null — neither uses this default).
 */
export function defaultAnswer(question: Question): AnswerValue {
  switch (question.type) {
    case "multi":
      return [];
    case "text":
      return "";
    case "number":
      return null;
    case "slider":
      return 0;
    default:
      return null;
  }
}
